// OWNER: Agent C. Static seed data for the MSW mock backend — realistic enough
// to design every screen against before the real Worker endpoints exist.
// Three scenarios: "default" (full portfolio), "stale" (every price >6h old,
// dashboard banner + badges must render), "empty" (zero holdings, zero loans).
import type { Account, Holding, Loan, LoanPayment, Transaction, User } from "@shared/types";

export type Scenario = "default" | "stale" | "empty";

export interface PriceEntry {
  price: number; // already normalised to major units (GBp/100 done upstream)
  currency: "INR" | "GBP" | "USD";
  source: string;
  ageMs: number; // how old this quote is, relative to "now"
}

const HOUR = 60 * 60 * 1000;

/** Native-currency prices, fresh in the "default"/"empty" scenarios. */
export const FRESH_PRICES: Record<string, PriceEntry> = {
  "122639": { price: 78.5432, currency: "INR", source: "mfapi", ageMs: 45 * 60 * 1000 },
  "118825": { price: 112.3401, currency: "INR", source: "mfapi", ageMs: 45 * 60 * 1000 },
  "125354": { price: 65.2187, currency: "INR", source: "mfapi", ageMs: 45 * 60 * 1000 },
  "108272": { price: 145.6723, currency: "INR", source: "mfapi", ageMs: 45 * 60 * 1000 },
  "120716": { price: 234.5609, currency: "INR", source: "mfapi", ageMs: 45 * 60 * 1000 },
  NET: { price: 92.14, currency: "USD", source: "yahoo", ageMs: 2 * HOUR },
  "VUSA.L": { price: 94.12, currency: "GBP", source: "yahoo", ageMs: 3 * HOUR },
  "VWRL.L": { price: 112.35, currency: "GBP", source: "yahoo", ageMs: 3 * HOUR },
  "VMID.L": { price: 24.87, currency: "GBP", source: "yahoo", ageMs: 3 * HOUR },
};

/** In the "stale" scenario, the index fund's price provider has been down for two days. */
export const STALE_SYMBOL = "120716";

export const FX_FRESH = { GBPINR: 107.01, USDINR: 87.45 };

export const now = () => Date.now();

// ── accounts ─────────────────────────────────────────────────────────────

export const ACCOUNTS: Account[] = [
  {
    id: 1,
    name: "Zerodha Coin",
    country: "IN",
    kind: "MUTUAL_FUND",
    currency: "INR",
    provider: "Zerodha",
    isActive: true,
    sortOrder: 0,
    createdAt: "2022-04-01T00:00:00.000Z",
  },
  {
    id: 2,
    name: "Employee Stock Option",
    country: "IN",
    kind: "ESOP",
    currency: "USD",
    provider: "Equity Edge",
    isActive: true,
    sortOrder: 1,
    createdAt: "2021-01-15T00:00:00.000Z",
  },
  {
    id: 3,
    name: "Chase Cash ISA",
    country: "UK",
    kind: "CASH_ISA",
    currency: "GBP",
    provider: "Chase UK",
    isActive: true,
    sortOrder: 2,
    createdAt: "2023-06-01T00:00:00.000Z",
  },
  {
    id: 4,
    name: "Trading 212 ISA",
    country: "UK",
    kind: "STOCKS_ISA",
    currency: "GBP",
    provider: "Trading 212",
    isActive: true,
    sortOrder: 3,
    createdAt: "2023-06-01T00:00:00.000Z",
  },
];

// ── holdings (per account) ──────────────────────────────────────────────

export const HOLDINGS: Holding[] = [
  {
    id: 101,
    accountId: 1,
    name: "Parag Parikh Flexi Cap - Direct Growth",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "MF_SCHEME_CODE",
    symbol: "122639",
    currency: "INR",
    units: 1834.221,
    investedAmount: 120000,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-07-01",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2022-04-02T00:00:00.000Z",
    updatedAt: "2026-07-01T00:00:00.000Z",
  },
  {
    id: 102,
    accountId: 1,
    name: "Mirae Asset Large Cap - Direct Growth",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "MF_SCHEME_CODE",
    symbol: "118825",
    currency: "INR",
    units: 892.774,
    investedAmount: 85000,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-06-15",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2022-05-10T00:00:00.000Z",
    updatedAt: "2026-06-15T00:00:00.000Z",
  },
  {
    id: 103,
    accountId: 1,
    name: "Axis Small Cap - Direct Growth",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "MF_SCHEME_CODE",
    symbol: "125354",
    currency: "INR",
    units: 1120.5,
    investedAmount: 60000,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-05-20",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2022-08-01T00:00:00.000Z",
    updatedAt: "2026-05-20T00:00:00.000Z",
  },
  {
    id: 104,
    accountId: 1,
    name: "HDFC Mid-Cap Opportunities - Direct Growth",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "MF_SCHEME_CODE",
    symbol: "108272",
    currency: "INR",
    units: 405.9,
    investedAmount: 45000,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-04-11",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2023-01-05T00:00:00.000Z",
    updatedAt: "2026-04-11T00:00:00.000Z",
  },
  {
    id: 105,
    accountId: 1,
    name: "ICICI Pru Nifty 50 Index - Direct Growth",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "MF_SCHEME_CODE",
    symbol: "120716",
    currency: "INR",
    units: 640.0,
    investedAmount: 130000,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-03-30",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2022-11-20T00:00:00.000Z",
    updatedAt: "2026-03-30T00:00:00.000Z",
  },
  {
    id: 201,
    accountId: 2,
    name: "Cloudflare (NET) RSUs",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "TICKER",
    symbol: "NET",
    currency: "USD",
    units: 240,
    investedAmount: 9600,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2025-09-15",
    strikePrice: 40,
    vestedUnits: 180,
    notes: "4-year vest, 25% cliff",
    isActive: true,
    createdAt: "2021-01-15T00:00:00.000Z",
    updatedAt: "2025-09-15T00:00:00.000Z",
  },
  {
    id: 301,
    accountId: 3,
    name: "Cash ISA balance",
    valuationMode: "CASH_BALANCE",
    symbolType: "NONE",
    symbol: null,
    currency: "GBP",
    units: 0,
    investedAmount: 15000,
    cashBalance: 15420.5,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-08-01",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2023-06-01T00:00:00.000Z",
    updatedAt: "2026-08-01T00:00:00.000Z",
  },
  {
    id: 401,
    accountId: 4,
    name: "Vanguard S&P 500 UCITS (VUSA)",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "TICKER",
    symbol: "VUSA.L",
    currency: "GBP",
    units: 62.311,
    investedAmount: 4800,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-07-20",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2023-06-05T00:00:00.000Z",
    updatedAt: "2026-07-20T00:00:00.000Z",
  },
  {
    id: 402,
    accountId: 4,
    name: "Vanguard FTSE All-World UCITS (VWRL)",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "TICKER",
    symbol: "VWRL.L",
    currency: "GBP",
    units: 38.9,
    investedAmount: 3900,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-06-28",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2023-07-01T00:00:00.000Z",
    updatedAt: "2026-06-28T00:00:00.000Z",
  },
  {
    id: 403,
    accountId: 4,
    name: "Vanguard FTSE 250 UCITS (VMID)",
    valuationMode: "UNITS_X_PRICE",
    symbolType: "TICKER",
    symbol: "VMID.L",
    currency: "GBP",
    units: 71.2,
    investedAmount: 1650,
    cashBalance: 0,
    manualValue: null,
    manualPrice: null,
    lastTransactionAt: "2026-02-14",
    strikePrice: null,
    vestedUnits: null,
    notes: null,
    isActive: true,
    createdAt: "2023-09-12T00:00:00.000Z",
    updatedAt: "2026-02-14T00:00:00.000Z",
  },
];

// ── loans ────────────────────────────────────────────────────────────────

export const LOANS: Loan[] = [
  {
    id: 1,
    name: "HDFC Home Loan",
    lender: "HDFC Ltd.",
    currency: "INR",
    principal: 4500000,
    outstandingPrincipal: 3218450.75,
    interestRate: 8.6,
    emiAmount: 41200,
    tenureMonths: 180,
    startDate: "2022-03-01",
    nextDueDate: "2026-09-05",
    status: "active",
    createdAt: "2022-03-01T00:00:00.000Z",
    updatedAt: "2026-08-05T00:00:00.000Z",
  },
];

export const LOAN_PAYMENTS: LoanPayment[] = [
  { id: 1, loanId: 1, paidOn: "2026-08-05", amount: 41200, principalComponent: 18100, interestComponent: 23100, isPrepayment: false, note: null, createdAt: "2026-08-05T00:00:00.000Z" },
  { id: 2, loanId: 1, paidOn: "2026-07-05", amount: 41200, principalComponent: 17970, interestComponent: 23230, isPrepayment: false, note: null, createdAt: "2026-07-05T00:00:00.000Z" },
  { id: 3, loanId: 1, paidOn: "2026-06-05", amount: 41200, principalComponent: 17840, interestComponent: 23360, isPrepayment: false, note: null, createdAt: "2026-06-05T00:00:00.000Z" },
];

// ── transactions (a handful of history rows per holding) ───────────────

export const TRANSACTIONS: Transaction[] = [
  { id: 1001, holdingId: 101, type: "BUY", txnDate: "2026-07-01", amount: 10000, units: 127.4, price: 78.49, currency: "INR", fxRateInr: 1, note: null, createdAt: "2026-07-01T09:00:00.000Z" },
  { id: 1002, holdingId: 101, type: "BUY", txnDate: "2026-06-01", amount: 10000, units: 129.1, price: 77.46, currency: "INR", fxRateInr: 1, note: null, createdAt: "2026-06-01T09:00:00.000Z" },
  { id: 1003, holdingId: 102, type: "BUY", txnDate: "2026-06-15", amount: 8000, units: 71.9, price: 111.27, currency: "INR", fxRateInr: 1, note: null, createdAt: "2026-06-15T09:00:00.000Z" },
  { id: 1004, holdingId: 105, type: "SELL", txnDate: "2026-03-30", amount: 15000, units: 63.2, price: 237.34, currency: "INR", fxRateInr: 1, note: "Rebalancing", createdAt: "2026-03-30T09:00:00.000Z" },
  { id: 1005, holdingId: 301, type: "INTEREST", txnDate: "2026-08-01", amount: 62.4, units: null, price: null, currency: "GBP", fxRateInr: 107.01, note: "Monthly interest", createdAt: "2026-08-01T09:00:00.000Z" },
  { id: 1006, holdingId: 401, type: "BUY", txnDate: "2026-07-20", amount: 500, units: 5.31, price: 94.16, currency: "GBP", fxRateInr: 107.01, note: null, createdAt: "2026-07-20T09:00:00.000Z" },
  { id: 1007, holdingId: 201, type: "VEST", txnDate: "2025-09-15", amount: 0, units: 60, price: null, currency: "USD", fxRateInr: 87.45, note: "Annual vest tranche", createdAt: "2025-09-15T09:00:00.000Z" },
];

export const USERS: User[] = [
  {
    id: 1,
    email: "amodhshenoy@gmail.com",
    name: "Amodh",
    role: "admin",
    status: "active",
    createdAt: "2021-01-01T00:00:00.000Z",
    lastLoginAt: "2026-08-22T08:12:00.000Z",
    emailVerifiedForDelivery: true,
  },
  {
    id: 2,
    email: "priya@example.com",
    name: "Priya",
    role: "member",
    status: "active",
    createdAt: "2023-02-10T00:00:00.000Z",
    lastLoginAt: "2026-08-20T14:03:00.000Z",
    emailVerifiedForDelivery: false,
  },
];
